import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Card, Flex, Image, Tooltip, getPaletteColor } from "pcln-design-system";
import { Dot } from "pcln-icons";
import { useEffect, useState } from "react";
import styled_components, { css, keyframes } from "styled-components";
import analytics from "./analytics/analytics.js";
import ErrorComponent from "./components/ErrorComponent/index.js";
import { useSuppressOnNativeWebView } from "./hooks/useSuppressOnNativeWebView.js";
import { ChatMessageErrorBoundary } from "./index.js";
import { fetchChatbotFeatures } from "./services/chatbotFeaturesService.js";
const TRAVEL_BOT_LOGO_PATH = 'https://s1.pclncdn.com/design-assets/pcln-bot/logos/';
const DEFAULT_TRAVEL_BOT_URI = 'pcln_bot_opener_logo_no_border_v1.1.svg';
const CircleCard = /*#__PURE__*/ styled_components(Card).withConfig({
    displayName: "StickyChatBotOpenerInner__CircleCard"
})([
    "width:60px;height:60px;background-color:",
    ";transition:0.2s ease-in-out;border:none;box-shadow:",
    ";&:hover{cursor:pointer;box-shadow:",
    ";}"
], getPaletteColor('primary.base'), ({ theme })=>theme.shadows.md, ({ theme })=>theme.shadows.lg);
const StyledDot = /*#__PURE__*/ styled_components(Dot).withConfig({
    displayName: "StickyChatBotOpenerInner__StyledDot"
})([
    "position:absolute;right:-8px;top:-5px;"
]);
const fadeInFromBottomToTopAnimation = (props)=>{
    const animation = /*#__PURE__*/ keyframes([
        "from{transform:translate(",
        ",",
        ");-webkit-transform:translate(",
        ",",
        ");opacity:0;}to{transform:translate(",
        ",",
        ");opacity:1;}"
    ], props.fromX, props.fromY, props.fromX, props.fromY, props.toX, props.toY);
    return /*#__PURE__*/ css([
        "position:relative;animation:",
        ";animation-duration:0.4s;animation-fill-mode:forwards;animation-timing-function:cubic-bezier(0,0,0.3,1);"
    ], animation);
};
const AnimateBottomToTop = /*#__PURE__*/ styled_components.div.withConfig({
    displayName: "StickyChatBotOpenerInner__AnimateBottomToTop"
})([
    "",
    ";position:absolute;bottom:0;width:100%;height:100%;"
], fadeInFromBottomToTopAnimation);
const AnimateComponent = ({ children, animationProperties, animationType })=>{
    if ('BottomToTop' === animationType) {
        const { bottom, from, to } = animationProperties;
        return /*#__PURE__*/ jsx(AnimateBottomToTop, {
            fromX: from.x,
            fromY: from.y,
            toX: to.x,
            toY: to.y,
            bottom: bottom,
            children: children
        });
    }
    return children;
};
const StickyChatBotOpenerInnerImpl = (props)=>{
    const { shouldSuppress } = useSuppressOnNativeWebView();
    const { isChatBoxOpened, onComponentRendered, onClickHandler, openerPosition, animationType = 'None', animationProperties, isMobile } = props;
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isSectionActive, setIsSectionActive] = useState(false);
    const [travelBotLogoUrl, setTravelBotLogoUrl] = useState(TRAVEL_BOT_LOGO_PATH);
    const [isConfigLoaded, setIsConfigLoaded] = useState(false);
    useEffect(()=>{
        let isMounted = true;
        const loadFeatures = async ()=>{
            const isBrowser = 'undefined' != typeof window;
            if (shouldSuppress || !isBrowser) {
                if (isMounted) {
                    const defaultUrl = `${TRAVEL_BOT_LOGO_PATH}${DEFAULT_TRAVEL_BOT_URI}`;
                    setTravelBotLogoUrl(defaultUrl);
                    setIsConfigLoaded(true);
                }
                return;
            }
            try {
                const features = await fetchChatbotFeatures();
                if (!isMounted) return;
                const variant = features?.configs?.chatBotConfigs?.chatbotFabVariantToUse;
                const chatBotUri = 'string' == typeof variant && '' !== variant.trim() ? variant : DEFAULT_TRAVEL_BOT_URI;
                const logoUrl = `${TRAVEL_BOT_LOGO_PATH}${chatBotUri}`;
                setTravelBotLogoUrl(logoUrl);
                setIsConfigLoaded(true);
            } catch (error) {
                analytics.log({
                    label: 'stickyChatBotOpen',
                    message: 'error in stickyChatBotOpen',
                    error
                });
                if (isMounted) {
                    const defaultUrl = `${TRAVEL_BOT_LOGO_PATH}${DEFAULT_TRAVEL_BOT_URI}`;
                    setTravelBotLogoUrl(defaultUrl);
                    setIsConfigLoaded(true);
                }
            }
        };
        loadFeatures();
        return ()=>{
            isMounted = false;
        };
    }, [
        shouldSuppress
    ]);
    useEffect(()=>{
        if (!isConfigLoaded || shouldSuppress) return;
        onComponentRendered();
    }, [
        isConfigLoaded,
        onComponentRendered,
        shouldSuppress
    ]);
    useEffect(()=>{
        if (!isConfigLoaded) return;
        if (isChatBoxOpened && !isSectionActive) setIsSectionActive(true);
    }, [
        isChatBoxOpened,
        isConfigLoaded,
        isSectionActive
    ]);
    const handleInteraction = ()=>{
        if (!isMobile) setIsTooltipOpen(true);
        const channel = new BroadcastChannel('penny-chatbot-events');
        channel.postMessage({
            type: 'retrieve-prompts'
        });
        setTimeout(()=>channel.close(), 100);
    };
    function onMouseOut() {
        setIsTooltipOpen(false);
    }
    const openerTooltipProps = props.openerTooltipProps || {};
    const tooltipProps = {
        top: true,
        center: true,
        bg: 'black',
        color: 'white',
        ...openerTooltipProps
    };
    if (shouldSuppress || !isConfigLoaded || !travelBotLogoUrl || travelBotLogoUrl.endsWith('/')) return null;
    return /*#__PURE__*/ jsx(AnimateComponent, {
        animationType: animationType,
        animationProperties: animationProperties,
        children: /*#__PURE__*/ jsxs("div", {
            className: "fixed bottom-4 right-4 h-[60px] w-[60px]",
            children: [
                /*#__PURE__*/ jsxs(CircleCard, {
                    style: {
                        overflow: 'hidden',
                        ...openerPosition
                    },
                    borderRadius: "full",
                    "data-testid": "openChatBotDialog",
                    onClick: onClickHandler,
                    onMouseOver: isMobile ? void 0 : handleInteraction,
                    onMouseOut: isMobile ? void 0 : onMouseOut,
                    onTouchStart: isMobile ? handleInteraction : void 0,
                    children: [
                        isTooltipOpen && /*#__PURE__*/ jsx(Tooltip, {
                            p: 1,
                            style: {
                                bottom: '-2px',
                                ...props.openerTooltipStyle
                            },
                            ...tooltipProps,
                            children: /*#__PURE__*/ jsx(Box, {
                                width: "110px",
                                children: "Chat with Penny"
                            })
                        }),
                        /*#__PURE__*/ jsx(Flex, {
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            style: {
                                overflow: 'hidden'
                            },
                            children: /*#__PURE__*/ jsx(Image, {
                                src: travelBotLogoUrl,
                                width: "100%",
                                height: "100%",
                                alt: "Penny chat opener",
                                style: {
                                    objectFit: 'contain',
                                    maxWidth: '100%',
                                    maxHeight: '100%'
                                }
                            })
                        })
                    ]
                }),
                isSectionActive && !isChatBoxOpened && /*#__PURE__*/ jsx(StyledDot, {
                    size: 32,
                    color: "warning.tone"
                })
            ]
        })
    });
};
const StickyChatBotOpenerInner = (props)=>/*#__PURE__*/ jsx(ChatMessageErrorBoundary, {
        FallbackComponent: /*#__PURE__*/ jsx(ErrorComponent, {}),
        compnentName: "StickyChatBotOpenerInner",
        children: /*#__PURE__*/ jsx(StickyChatBotOpenerInnerImpl, {
            ...props
        })
    });
export { StickyChatBotOpenerInner };
